// Unified recovery for "the build changed under us" failures.
//
// After a redeploy the previous build's hashed chunks no longer exist on the
// server, so any open session's next lazy import 404s. Every consumer of
// chunk-failure handling (the vite:preloadError listener, lazyRoute /
// React.lazy wrappers, the root error boundary) goes through THIS module so
// there is exactly one reload budget and one user-visible behavior:
// attempt 1 reloads immediately, attempt 2 shows the overlay and reloads
// after a beat, attempt 3+ gives up and lets the caller render a real error
// screen. All paths end in visible UI — never a silent blank page.
//
// Plain DOM (no React) on purpose: it must work before React mounts and
// while React is unmounting after a render error.

const STORE_KEY = "arego_chunk_recovery";
const WINDOW_MS = 60_000; // failures older than this start a fresh budget
const MAX_ATTEMPTS = 2;

export function isChunkLoadError(err) {
  const msg = String(err?.message || err || "");
  return /dynamically imported module|Importing a module script failed|error loading dynamically|Failed to fetch dynamically|Unable to preload CSS/i.test(msg);
}

const readState = () => {
  try {
    const s = JSON.parse(sessionStorage.getItem(STORE_KEY) || "null");
    if (s && typeof s.n === "number" && typeof s.at === "number") return s;
  } catch { /* fall through */ }
  return { n: 0, at: 0 };
};

const writeState = (s) => {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch { /* private mode */ }
};

// Full-screen "Updating…" overlay, injected with plain DOM so it renders even
// with no React tree. Idempotent.
export function showUpdatingOverlay() {
  if (document.getElementById("agp-update-overlay")) return;
  const el = document.createElement("div");
  el.id = "agp-update-overlay";
  el.setAttribute("style",
    "position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;" +
    "background:#faf8f4;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;");
  el.innerHTML =
    '<style>@keyframes agp-spin{to{transform:rotate(360deg)}}</style>' +
    '<div style="background:#fff;border:1px solid #e8e6df;border-radius:16px;padding:28px 40px;text-align:center;box-shadow:0 6px 24px rgba(0,0,0,.06)">' +
    '<div style="width:28px;height:28px;margin:0 auto 14px;border:3px solid #e8e6df;border-top-color:#4263eb;border-radius:50%;animation:agp-spin .8s linear infinite"></div>' +
    '<div style="font-weight:700;color:#1d1c19">Updating to the latest version…</div>' +
    '<div style="font-size:13px;color:#8a8a83;margin-top:4px">Just a second.</div></div>';
  (document.body || document.documentElement).appendChild(el);
}

// Returns true when a recovery reload has been initiated (the page is about
// to be replaced); false when the budget is exhausted and the caller should
// surface a visible error instead.
export function attemptRecoveryReload() {
  const now = Date.now();
  let s = readState();
  if (now - s.at > WINDOW_MS) s = { n: 0, at: now };
  if (s.n >= MAX_ATTEMPTS) return false;
  writeState({ n: s.n + 1, at: s.at || now });
  showUpdatingOverlay();
  if (s.n === 0) {
    window.location.reload();
  } else {
    // Second try: give the deploy a beat to finish swapping containers.
    setTimeout(() => window.location.reload(), 2000);
  }
  return true;
}

// Wraps a dynamic import. Guarantees a non-null module or a thrown error —
// and turns chunk-load failures into the shared recovery flow.
export async function loadChunk(importer) {
  let mod;
  try {
    mod = await importer();
  } catch (err) {
    if (isChunkLoadError(err) && attemptRecoveryReload()) {
      // The page is being replaced — never resolve so nothing renders.
      return new Promise(() => {});
    }
    throw err;
  }
  if (mod == null) {
    // vite:preloadError + preventDefault makes Vite RESOLVE the import with
    // undefined. Treat it exactly like the failure it is (previously this
    // surfaced as "Cannot read properties of undefined" and bypassed
    // recovery entirely).
    if (attemptRecoveryReload()) return new Promise(() => {});
    throw new Error("Failed to fetch dynamically imported module (resolved empty after preload error)");
  }
  return mod;
}
